/* ================================================= */
/* PASTORAL ATTENTION ENGINE                         */
/* Signal collapsing — visual calm — quiet states   */
/*                                                   */
/* Core principle: not everything deserves           */
/* simultaneous visual attention.                    */
/*                                                   */
/* Rules are ADDITIVE suppressions, not removals.   */
/* Information is preserved. Display is reduced.    */
/*                                                   */
/* Designed for extension into:                      */
/* - AI assistant briefing context                   */
/* - Voice pastoral assistant                        */
/* - Weekly digest mode                              */
/* - Mobile-first calm mode                          */
/* ================================================= */

import { WORKFLOW_TYPES, WORKFLOW_URGENCY } from "./pastoral-workflows";
import { TREND_TYPES } from "./pastoral-memory";

/* ============================= */
/* ATTENTION LEVELS              */
/* For future assistant context  */
/* and briefing mode             */
/* ============================= */

export const ATTENTION_LEVELS = {
  CALM:          0, // everything healthy — UI is quiet
  INFORMATIONAL: 1, // trend data available, no urgency
  ACTIONABLE:    2, // clear next action suggested
  URGENT:        3, // immediate attention needed
  CRITICAL:      4, // crisis response required
};

/* ============================= */
/* INTERNAL CONSTANTS            */
/* ============================= */

// Stages that make WorkflowStrip visible
const VISIBLE_WORKFLOW_STAGES = new Set(["initiated", "active", "recovering", "stalled"]);

// Visit-related workflow types — these subsume visit alerts
const VISIT_WORKFLOW_TYPES = new Set([
  WORKFLOW_TYPES.VISIT_FOLLOWUP,
  WORKFLOW_TYPES.RECOVERY_PATH,
]);

// Visit alert message fragments — used to identify suppressible alerts
const VISIT_ALERT_PATTERNS = ["Sin visita hace", "Sin registro de visita"];

/* ============================= */
/* SHOULD SHOW PASTORAL INSIGHT  */
/* ============================= */

/**
 * Determines whether PastoralInsight should render alongside WorkflowStrip.
 *
 * Suppression rule:
 * WorkflowStrip = trend state + actionable suggestion (a superset of PastoralInsight).
 * When a workflow is actively rendering, PastoralInsight is redundant.
 * Showing both creates adjacent strips that say the same thing twice.
 *
 * The STABLE_HEALTHY + no-workflow case is the ideal: PastoralInsight
 * appears alone as a calm, positive signal — "Seguimiento constante y saludable".
 * That's where it shines. Don't compete with the strip below it.
 *
 * @param {Object}     memory    — from buildPastoralMemory()
 * @param {Workflow[]} workflows — from detectActiveWorkflows()
 * @returns {boolean}
 */
export function shouldShowPastoralInsight(memory, workflows) {
  if (!memory?.momentum) return false;

  const { confidence, trend } = memory.momentum;
  if (confidence < 0.6)               return false;
  if (trend === TREND_TYPES.UNKNOWN)   return false;

  // Don't show when WorkflowStrip is also visible — it already subsumes the insight
  const hasVisibleWorkflow = (workflows || []).some(
    (w) => VISIBLE_WORKFLOW_STAGES.has(w.stage)
  );
  return !hasVisibleWorkflow;
}

/* ============================= */
/* SUPPRESS VISIT ALERTS         */
/* ============================= */

/**
 * Filters out visit-type AlertStrips when WorkflowStrip already covers them.
 *
 * Suppression rule:
 * "Sin visita hace X días (red)" + "El seguimiento perdió continuidad" = saying the
 * same thing twice. When a VISIT_FOLLOWUP or RECOVERY_PATH workflow is visibly
 * rendering, the visit alert is redundant and adds noise.
 *
 * Prayer alerts and member-state alerts are NEVER suppressed — they carry
 * distinct pastoral information that WorkflowStrip doesn't fully capture.
 *
 * @param {Alert[]}    alerts    — from evaluatePersonRisk / evaluateFamilyRisk
 * @param {Workflow[]} workflows — from detectActiveWorkflows()
 * @returns {Alert[]} filtered alerts
 */
export function suppressVisitAlerts(alerts, workflows) {
  if (!alerts || alerts.length === 0) return alerts;

  const hasVisitWorkflow = (workflows || []).some(
    (w) => VISIT_WORKFLOW_TYPES.has(w.type) && VISIBLE_WORKFLOW_STAGES.has(w.stage)
  );
  if (!hasVisitWorkflow) return alerts;

  return alerts.filter(
    (a) => !VISIT_ALERT_PATTERNS.some((pattern) => (a.message || "").includes(pattern))
  );
}

/* ============================= */
/* COMPUTE ATTENTION LEVEL       */
/* For future assistant context  */
/* ============================= */

/**
 * Derives the aggregate attention level from current alerts + workflows.
 * Returns an ATTENTION_LEVELS value suitable for:
 * - AI assistant briefing context ("the pastor's current focus level is urgent")
 * - Voice assistant tone calibration
 * - Weekly digest priority tier
 * - Mobile-first calm mode threshold
 *
 * NOT used for visual rendering — use the specific suppression functions above.
 *
 * @param {Alert[]}    alerts    — from evaluator results
 * @param {Workflow[]} workflows — from detectActiveWorkflows()
 * @returns {number} ATTENTION_LEVELS value
 */
export function computeAttentionLevel(alerts, workflows) {
  const activeWorkflows = (workflows || []).filter(
    (w) => VISIBLE_WORKFLOW_STAGES.has(w.stage)
  );

  const hasCriticalAlert    = (alerts || []).some((a) => a.type === "red");
  const hasCriticalWorkflow = activeWorkflows.some((w) => w.urgency === WORKFLOW_URGENCY.CRITICAL);

  if (hasCriticalAlert || hasCriticalWorkflow) return ATTENTION_LEVELS.CRITICAL;

  const hasHighWorkflow = activeWorkflows.some((w) => w.urgency === WORKFLOW_URGENCY.HIGH);
  if (hasHighWorkflow) return ATTENTION_LEVELS.URGENT;

  if (activeWorkflows.length > 0) return ATTENTION_LEVELS.ACTIONABLE;

  const hasAmberAlert = (alerts || []).some((a) => a.type === "amber");
  if (hasAmberAlert) return ATTENTION_LEVELS.INFORMATIONAL;

  return ATTENTION_LEVELS.CALM;
}
